// Save-conflict parsing for the Agent Builder (Bug: save-conflict dead-end).
//
// The atomic tree save (`PUT /api/agents/:id/tree`) returns HTTP 412 with a
// structured body when the caller's `expected_tree_version` / `expected_updated_at`
// is stale (another editor — or another tab — saved first). The api client
// (`request()`) attaches the parsed JSON body to the thrown error as `err.body`,
// so `err.body.detail` is the `{error, current_version, current_updated_at, ...}`
// object the backend raised.
//
// Pure + side-effect-free so it is fully unit-testable outside the React shell.
// The Builder only ever sends `expected_tree_version`, so `stale_tree_version`
// (which carries `current_version`) is the case it can actually overwrite; a
// `stale_updated_at` conflict is still surfaced but not blindly overwritable
// (no version token to refresh to), so `canOverwrite` is false there.

const CONFLICT_MESSAGE =
  "Another editor saved a newer version of this agent. " +
  "Overwrite keeps your canvas changes; to load their version instead, reload the page.";

/**
 * Parse a save error into a structured version-conflict descriptor, or null
 * when the error is not a version conflict.
 *
 * @param {*} err  the error thrown by agentApi.putTree (may be anything)
 * @returns {null | {kind: string, currentVersion: number|null, currentUpdatedAt: string|null, canOverwrite: boolean, message: string}}
 */
export function describeSaveConflict(err) {
  const detail = err && err.body && err.body.detail;
  if (!detail || typeof detail !== "object") return null;

  if (detail.error === "stale_tree_version") {
    const currentVersion =
      typeof detail.current_version === "number" ? detail.current_version : null;
    return {
      kind: "stale_tree_version",
      currentVersion,
      currentUpdatedAt: detail.current_updated_at || null,
      canOverwrite: currentVersion !== null,
      message: CONFLICT_MESSAGE,
    };
  }

  if (detail.error === "stale_updated_at") {
    return {
      kind: "stale_updated_at",
      currentVersion: null,
      currentUpdatedAt: detail.current_updated_at || null,
      canOverwrite: false,
      message: CONFLICT_MESSAGE,
    };
  }

  return null;
}
